import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Award, RefreshCw, Loader2, X, ShieldCheck, ShieldX, TrendingUp, Filter } from "lucide-react";

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

const FILTERS = [
  ["all", "All"], ["open_to_bid", "Open to bid"],
  ["license_active", "Licensed"], ["elite", "Elite"],
  ["awaiting_scrutiny", "Awaiting scrutiny"],
];

export function AdminSellerRanking({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/rfq-distribution");
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const arr = data?.sellers || [];
    if (filter === "all") return arr;
    if (filter === "open_to_bid") return arr.filter(s => s.open_to_bid);
    if (filter === "license_active") return arr.filter(s => s.license_active);
    if (filter === "elite") return arr.filter(s => s.elite_status === "elite");
    if (filter === "awaiting_scrutiny") return arr.filter(s => s.elite_status === "awaiting_scrutiny");
    return arr;
  }, [data, filter]);

  return (
    <div data-testid="admin-seller-ranking" className="space-y-6">
      <div className="flex items-start gap-4 border-b border-white/10 pb-6">
        <div className="h-12 w-12 rounded-sm bg-[#D8B23E]/15 text-[#D8B23E] flex items-center justify-center shrink-0">
          <TrendingUp size={20}/>
        </div>
        <div className="flex-1">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Seller Ranking · RFQ Distribution</div>
          <h2 className="font-serif text-2xl text-[#F5F5F0] mt-1">Highest commission first · Elite tie-breaker.</h2>
          <p className="font-body text-xs text-[#8A8A82] mt-1">
            Ranking key: Open-to-bid → Commission % desc → License active → Elite → Business name.
            Applies to new RFQ broadcasts.
          </p>
        </div>
        <button onClick={load} disabled={loading} className="text-[#8A8A82] hover:text-[#F5F5F0] p-1" data-testid="ranking-refresh">
          {loading ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] inline-flex items-center gap-1 pr-2">
          <Filter size={11}/> Filter
        </span>
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`ranking-filter-${k}`}
                  className={`font-grotesk text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-sm border ${
                    filter === k ? "bg-[#D8B23E] text-black border-[#D8B23E]" : "border-white/15 text-[#8A8A82] hover:text-[#F5F5F0]"
                  }`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-10 text-center font-body text-sm text-[#8A8A82]">
          <Loader2 size={16} className="inline animate-spin mr-2"/> Loading ranking…
        </div>
      ) : rows.length === 0 ? (
        <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-10 text-center font-body text-sm text-[#8A8A82]">
          No sellers match the filter.
        </div>
      ) : (
        <div className="border border-white/10 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-black/40">
              <tr>
                {["#", "Seller", "State", "Commission", "Open", "License", "Elite"].map(h => (
                  <th key={h} className="text-left font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] px-3 py-2 border-b border-white/10">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((s, idx) => (
                <tr key={s.seller_id} data-testid={`seller-rank-${idx}`}
                    onClick={() => setActive(s)}
                    className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer">
                  <td className="px-3 py-3 font-mono text-[#D8B23E]">#{idx + 1}</td>
                  <td className="px-3 py-3">
                    <div className="font-body text-[13px] text-[#F5F5F0]">{s.business_name || s.email}</div>
                    <div className="font-body text-[10px] text-[#8A8A82]">{s.email}</div>
                  </td>
                  <td className="px-3 py-3 font-body text-[12px] text-[#C9C9C2]">{s.state || "—"}</td>
                  <td className="px-3 py-3 font-mono text-[13px] text-[#D8B23E]">{s.commission_offer_pct.toFixed(1)}%</td>
                  <td className="px-3 py-3">
                    {s.open_to_bid ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-[#3DBB84]/40 text-[#3DBB84] px-2 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.18em]">Live</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full border border-white/15 text-[#8A8A82] px-2 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.18em]">Off</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    {s.license_active ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-[#3DBB84]/40 text-[#3DBB84] px-2 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.18em]">
                        <ShieldCheck size={9}/> {s.license_days_left}d
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full border border-[#E5484D]/40 text-[#E5484D] px-2 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.18em]">
                        <ShieldX size={9}/> Locked
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <EliteBadge status={s.elite_status}/>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AnimatePresence>
        {active && <EliteDecisionDrawer api={api} seller={active}
                                        onClose={() => setActive(null)}
                                        onUpdated={() => { load(); setActive(null); }}/>}
      </AnimatePresence>
    </div>
  );
}

function EliteBadge({ status }) {
  if (status === "elite") return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[#D8B23E]/60 text-[#D8B23E] px-2 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.18em]">
      <Award size={9}/> Elite
    </span>
  );
  if (status === "awaiting_scrutiny") return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[#B87333]/60 text-[#B87333] px-2 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.18em]">
      Awaiting
    </span>
  );
  if (status === "rejected") return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[#E5484D]/40 text-[#E5484D] px-2 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.18em]">
      Rejected
    </span>
  );
  return <span className="font-body text-[10px] text-[#8A8A82]">—</span>;
}

function EliteDecisionDrawer({ api, seller, onClose, onUpdated }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const decide = async (decision) => {
    if (decision === "rejected" && !reason.trim()) { toast.error("Reason required for rejection"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/sellers/${seller.seller_id}/elite/decide`,
                     { decision, reason: reason.trim() });
      toast.success(decision === "elite" ? "Elite membership approved · certificate issued" : "Application rejected");
      onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const canDecide = seller.elite_status === "awaiting_scrutiny";

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-stretch justify-end"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }}
        className="bg-[#050506] w-full max-w-xl border-l border-[#D8B23E]/30 h-full overflow-y-auto"
        data-testid="seller-decision-drawer">
        <div className="sticky top-0 z-10 bg-[#050506]/95 backdrop-blur flex items-center justify-between border-b border-white/10 px-6 py-4">
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Seller Profile</div>
            <div className="font-serif text-xl text-[#F5F5F0]">{seller.business_name || seller.email}</div>
            <div className="font-body text-[11px] text-[#8A8A82]">{seller.email} · {seller.state}</div>
          </div>
          <button onClick={onClose} className="text-[#8A8A82] hover:text-[#F5F5F0]" data-testid="drawer-close"><X size={20}/></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            {[
              ["Commission offer", `${seller.commission_offer_pct.toFixed(1)}%`],
              ["Daily quota", seller.daily_quotation_limit],
              ["License", seller.license_active ? `active · ${seller.license_days_left}d left` : "locked"],
              ["Open to bid", seller.open_to_bid ? "YES" : "no"],
              ["Elite status", seller.elite_status.replace(/_/g, " ")],
              ["Cert #", seller.elite_certificate_no || "—"],
            ].map(([k, v]) => (
              <div key={k} className="border border-white/10 rounded-sm bg-[#0a0a0a] p-3">
                <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">{k}</div>
                <div className="font-body text-sm text-[#F5F5F0] mt-1 capitalize">{v}</div>
              </div>
            ))}
          </div>

          {seller.geo_territories?.length > 0 && (
            <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-4">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E] mb-2">Territories</div>
              <div className="space-y-1">
                {seller.geo_territories.map((t, i) => (
                  <div key={i} className="font-body text-sm text-[#F5F5F0]">
                    <span className="text-[#D8B23E]">{t.state}</span>
                    {t.cities?.length ? <span className="text-[#8A8A82]"> · {t.cities.join(", ")}</span> : null}
                  </div>
                ))}
              </div>
            </div>
          )}

          {canDecide ? (
            <div className="border border-[#D8B23E]/40 bg-[#D8B23E]/[0.05] rounded-sm p-4">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E] mb-2">Elite decision</div>
              <textarea data-testid="decide-reason" value={reason} onChange={e => setReason(e.target.value)}
                        rows={3} placeholder="Reason (required only for rejection)"
                        className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/>
              <div className="flex gap-3 mt-3">
                <button onClick={() => decide("elite")} disabled={busy} data-testid="decide-approve"
                        className="inline-flex items-center gap-2 bg-[#3DBB84] hover:bg-[#2fa16e] disabled:opacity-40 text-black font-grotesk text-[10px] uppercase tracking-[0.24em] px-4 py-2 rounded-sm">
                  {busy ? <Loader2 size={12} className="animate-spin"/> : <Award size={12}/>} Approve · issue certificate
                </button>
                <button onClick={() => decide("rejected")} disabled={busy} data-testid="decide-reject"
                        className="inline-flex items-center gap-2 border border-[#E5484D]/50 text-[#E5484D] hover:bg-[#E5484D]/10 disabled:opacity-40 font-grotesk text-[10px] uppercase tracking-[0.24em] px-4 py-2 rounded-sm">
                  Reject
                </button>
              </div>
            </div>
          ) : (
            <div className="font-body text-[11px] text-[#8A8A82]">
              Seller is not currently in scrutiny. Applications can be reviewed only in the &lsquo;Awaiting scrutiny&rsquo; state.
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
